import { 
    collection, 
    doc, 
    setDoc, 
    getDoc, 
    updateDoc, 
    query, 
    where, 
    getDocs,
    serverTimestamp,
    increment
  } from 'firebase/firestore'
  import { db } from '../lib/firebase'
  import { nanoid } from 'nanoid'
  
  class QRService {
    constructor() {
      this.qrCollection = collection(db, 'qr_codes')
      this.objectsCollection = collection(db, 'objects')
      this.scansCollection = collection(db, 'scans')
      this.relayPointsCollection = collection(db, 'relay_points')
    }
  
    // Générer un nouveau QR code
    async generateQRCode(adminId, batchSize = 1) {
      const qrCodes = []
      
      for (let i = 0; i < batchSize; i++) {
        const qrId = nanoid(10) // ID unique de 10 caractères
        const qrCode = {
          id: qrId,
          adminId,
          createdAt: serverTimestamp(),
          status: 'unassigned', // unassigned, assigned, active, lost, found
          url: `${window.location.origin}/scan/${qrId}`,
          stats: {
            scans: 0,
            firstScanAt: null,
            lastScanAt: null
          }
        }
        
        await setDoc(doc(this.qrCollection, qrId), qrCode)
        qrCodes.push(qrCode)
      }
      
      return qrCodes
    }
  
    // Assigner un QR code à un objet
    async assignQRCode(qrId, userId, objectData) {
      const qrRef = doc(this.qrCollection, qrId)
      const qrDoc = await getDoc(qrRef)
      
      if (!qrDoc.exists()) {
        throw new Error('QR code non trouvé')
      }
      
      if (qrDoc.data().status !== 'unassigned') {
        throw new Error('QR code déjà assigné')
      }
      
      // Créer l'objet dans la collection objects
      const objectRef = doc(this.objectsCollection)
      const object = {
        id: objectRef.id,
        qrId,
        userId,
        ...objectData,
        createdAt: serverTimestamp(),
        status: 'active',
        lastSeen: null,
        foundAt: null,
        relayPointId: null
      }
      
      await setDoc(objectRef, object)
      
      // Mettre à jour le QR code
      await updateDoc(qrRef, {
        status: 'assigned',
        objectId: objectRef.id,
        userId,
        assignedAt: serverTimestamp()
      })
      
      return object
    }
  
    // Scanner un QR code (premier scan = assignation, suivant = signalement)
    async scanQRCode(qrId, scanData) {
      const qrRef = doc(this.qrCollection, qrId)
      const qrDoc = await getDoc(qrRef)
      
      if (!qrDoc.exists()) {
        throw new Error('QR code non trouvé')
      }
      
      const qrData = qrDoc.data()
      const scanRef = doc(this.scansCollection)
      
      // Enregistrer le scan
      const scan = {
        id: scanRef.id,
        qrId,
        timestamp: serverTimestamp(),
        ...scanData,
        userAgent: navigator.userAgent,
        ip: await this.getClientIP()
      }
      
      await setDoc(scanRef, scan)
      
      // Mettre à jour les statistiques du QR code
      await updateDoc(qrRef, {
        'stats.scans': increment(1),
        'stats.lastScanAt': serverTimestamp(),
        'stats.firstScanAt': qrData.stats.firstScanAt || serverTimestamp()
      })
      
      // Si c'est le premier scan et non assigné
      if (qrData.status === 'unassigned' && qrData.stats.scans === 0) {
        return {
          type: 'first_scan',
          qrId,
          status: 'ready_for_assignment',
          message: 'Ce QR code est prêt à être assigné à un objet'
        }
      }
      
      // Si l'objet est assigné et actif
      if (qrData.status === 'assigned' && qrData.objectId) {
        const objectRef = doc(this.objectsCollection, qrData.objectId)
        const objectDoc = await getDoc(objectRef)
        
        if (objectDoc.exists()) {
          const objectData = objectDoc.data()
          
          // Si l'objet n'est pas encore perdu
          if (objectData.status === 'active') {
            return {
              type: 'checkin',
              object: objectData,
              message: 'Objet vérifié à cet emplacement'
            }
          }
          
          // Si l'objet est marqué comme perdu
          if (objectData.status === 'lost') {
            return {
              type: 'found',
              object: objectData,
              message: 'Cet objet a été signalé comme perdu. Voulez-vous aider ?'
            }
          }
        }
      }
      
      return {
        type: 'unknown',
        qrData,
        message: 'Scan enregistré'
      }
    }
  
    // Signaler un objet comme trouvé
    async reportObjectFound(qrId, finderData, action) {
      const qrRef = doc(this.qrCollection, qrId)
      const qrDoc = await getDoc(qrRef)
      
      if (!qrDoc.exists() || !qrDoc.data().objectId) {
        throw new Error('Objet non trouvé')
      }
      
      const objectRef = doc(this.objectsCollection, qrDoc.data().objectId)
      
      const updates = {
        status: 'found',
        foundAt: serverTimestamp(),
        finderContact: finderData.contact,
        finderMessage: finderData.message,
        lastSeen: {
          location: finderData.location,
          timestamp: serverTimestamp()
        }
      }
      
      if (action === 'deposit_relay') {
        updates.relayPointId = finderData.relayPointId
        updates.depositStatus = 'pending'
      } else {
        updates.finderAction = 'contact_owner'
      }
      
      await updateDoc(objectRef, updates)
      await updateDoc(qrRef, { status: 'found' })
      
      // Récupérer les données de l'objet pour l'envoi de SMS
      const objectDoc = await getDoc(objectRef)
      const objectData = objectDoc.data()
      
      return {
        success: true,
        object: objectData,
        action
      }
    }
  
    // Récupérer les points relais proches
    async getNearbyRelayPoints(latitude, longitude, radiusKm = 5) {
      // Note: Pour une recherche géospatiale précise, il faudrait utiliser
      // les index géographiques de Firestore
      const relayPointsQuery = query(this.relayPointsCollection, where('active', '==', true))
      const snapshot = await getDocs(relayPointsQuery)
      
      const relayPoints = []
      snapshot.forEach(doc => {
        const point = doc.data()
        
        // Calcul de distance simple (Haversine)
        if (point.location && latitude && longitude) {
          const distance = this.calculateDistance(
            latitude,
            longitude,
            point.location.latitude,
            point.location.longitude
          )
          
          if (distance <= radiusKm) {
            relayPoints.push({
              ...point,
              id: doc.id,
              distance: Math.round(distance * 10) / 10 // Arrondi à 1 décimale
            })
          }
        } else {
          relayPoints.push({
            ...point,
            id: doc.id
          })
        }
      })
      
      // Trier par distance
      return relayPoints.sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity))
    }
  
    // Méthodes utilitaires
    calculateDistance(lat1, lon1, lat2, lon2) {
      const R = 6371 // Rayon de la Terre en km
      const dLat = this.deg2rad(lat2 - lat1)
      const dLon = this.deg2rad(lon2 - lon1)
      const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) * 
        Math.sin(dLon/2) * Math.sin(dLon/2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
      return R * c
    }
  
    deg2rad(deg) {
      return deg * (Math.PI/180)
    }
  
    async getClientIP() {
      try {
        const response = await fetch('https://api.ipify.org?format=json')
        const data = await response.json()
        return data.ip
      } catch (error) {
        return 'unknown'
      }
    }
  }
  
  export const qrService = new QRService()